import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order } from '@lr/shared';
import { apiFetch } from '../../lib/api';

type Row = Order & {
  user?: { email: string; first_name: string | null; last_name: string | null } | null;
  bundle_name?: string | null;
  credits_issued?: number;
};

export function AdminCreditPurchases() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<Row[]>('/api/admin/credit-purchases')
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);

  const totalRevenue = rows.reduce((sum, r) => sum + r.bundle_amount_pence, 0);
  // Sum the credits actually issued via credit_transactions (returned by
  // the API as `credits_issued`). orders.credits_used is for the rental
  // flow and is always 0 for bundle purchases.
  const totalCredits = rows.reduce((sum, r) => sum + (r.credits_issued ?? 0), 0);

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Credit sales</h1>
          <div className="sub">
            Bundle purchases — these top up the customer&apos;s credit balance.
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="lbl">Sales</div>
          <div className="val">{rows.length}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Revenue</div>
          <div className="val">£{(totalRevenue / 100).toFixed(2)}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Credits issued</div>
          <div className="val">{totalCredits}</div>
        </div>
      </div>

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">No credit purchases yet</div>
            <div className="p">Once customers buy a credit bundle, sales appear here.</div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th style={{ width: 100 }}>Credits</th>
                  <th style={{ width: 120 }}>Amount</th>
                  <th style={{ width: 130 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td className="id">{o.order_number}</td>
                    <td>
                      <Link to={`/admin/customers/${o.user_id}`}>
                        <div style={{ fontWeight: 600 }}>
                          {(o.user?.first_name ?? '') + ' ' + (o.user?.last_name ?? '')}
                        </div>
                        <div className="id">{o.user?.email}</div>
                      </Link>
                    </td>
                    <td className="num">{o.credits_issued ?? 0}</td>
                    <td className="num">£{(o.bundle_amount_pence / 100).toFixed(2)}</td>
                    <td>{new Date(o.created_at).toLocaleDateString('en-GB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
